import React, { useEffect, useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { User } from '@supabase/supabase-js';
import { supabase } from './lib/supabase';
import { Navigation } from './components/Navigation';
import { LoginForm } from './components/LoginForm';
import { Dashboard } from './pages/Dashboard';
import { Profile } from './pages/Profile';
import { VerifyResetToken } from './pages/VerifyResetToken';
import { ResetPasswordForm } from './pages/ResetPasswordForm';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import { captureSystemError } from './utils/errorReporting';

function NavigationWrapper({ user }: { user: User | null }) {
  const location = useLocation();
  const hideOnRoutes = ['/auth/confirm', '/reset-password/new'];
  const shouldHide = !user || hideOnRoutes.includes(location.pathname);

  return shouldHide ? null : <Navigation />;
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const verifyUserAccess = async (authUser: User, session: any = null) => {
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('Database query timeout'));
      }, 10000);
    });
    
    try {
      if (session) {
        supabase.rest.headers['Authorization'] = `Bearer ${session.access_token}`;
      }
      
      const simpleTestPromise = supabase.from('members').select('count', { count: 'exact' }).limit(1);
      
      try {
        await Promise.race([simpleTestPromise, timeoutPromise]);
      } catch (simpleError) {
        throw simpleError;
      }
      
      const queryPromise = supabase
        .from('members')
        .select('community_status, coach_status')
        .eq('user_id', authUser.id)
        .single();
      
      const result = await Promise.race([queryPromise, timeoutPromise]);
      const { data: member, error: memberError } = result as any;
      
      if (memberError) {
        setAuthError('Access restricted to Skip Community or Coach members');
        return null;
      }
      
      const hasValidCommunityStatus = member?.community_status === 'active' || member?.community_status === 'onboarding';
      const hasValidCoachStatus = member?.coach_status === 'active' || member?.coach_status === 'onboarding';
      
      if (!hasValidCommunityStatus && !hasValidCoachStatus) {
        setAuthError('Access restricted to active Skip Community or Coach members');
        return null;
      }
      
      setAuthError(null);
      return authUser;
    } catch (error) {
      if (error instanceof Error && error.message === 'Database query timeout') {
        // Capture timeout errors as these are system issues
        captureSystemError(error, {
          operation: 'auth_verification',
          action: 'verify_user_access',
          userId: authUser.id,
          metadata: {
            errorType: 'timeout',
            timeoutDuration: 10000,
          },
        });
        setAuthError(null);
        return authUser;
      } else {
        // This is likely a permission error, not a system error
        setAuthError('Access restricted to Skip Community or Coach members');
        return null;
      }
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setAuthError(null);
        setLoading(false);
        return;
      }
      
      if (event === 'PASSWORD_RECOVERY' || window.location.pathname === '/reset-password/new') {
        setUser(session?.user || null);
        setLoading(false);
        return;
      }

      setTimeout(async () => {
        if (session?.user) {
          const verifiedUser = await verifyUserAccess(session.user, session);
          setUser(verifiedUser);
        } else {
          setUser(null);
        }
        setLoading(false);
      }, 0);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return <LoadingSpinner message="Loading..." />;
  }

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <NavigationWrapper user={user} />
        <Routes>
        {/* Public routes */}
        <Route path="/auth/confirm" element={<VerifyResetToken />} />
        <Route path="/reset-password/new" element={<ResetPasswordForm />} />

        {/* Sign-in page (shown only if not authenticated) */}
        {!user && (
          <Route
            path="/"
            element={
              <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
                <div className="w-full max-w-md">
                  <div className="flex flex-col items-center mb-8">
                    <img
                      src="https://data.skip.community/storage/v1/object/public/website//Skip%20logo.svg"
                      alt="Skip Logo"
                      className="h-20 mb-4"
                    />
                    <h1 className="text-2xl font-bold text-gray-900">Skip Community</h1>
                  </div>
                  <LoginForm 
                    initialError={authError || undefined} 
                    onClearError={() => setAuthError(null)}
                  />
                </div>
              </div>
            }
          />
        )}

        {/* Protected routes */}
        {user && (
          <>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/profile" element={<Profile />} />
          </>
        )}

        {/* Fallback route */}
        <Route
          path="*"
          element={
            user ? <Navigate to="/dashboard" replace /> : <Navigate to="/" replace />
          }
        />
      </Routes>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;